import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as provider from "../services/providerAPI";
import * as byodLogin from "../services/byodLoginAPI";
import getDropDownOptions from "../utils/constants/SelectVariables";
const _ = require("lodash");

//* Provider Details
export const useGetProviderDetails = (params) => {
  return useQuery({
    queryKey: ["Provider-Details", params],
    queryFn: () => provider.getProviderDetails({ userId: params }),
    enabled: !_.isEmpty(params),
    retry: false,
    cacheTime: 0,
  });
};

//* Provider Bank Details
export const useGetProviderBankDetails = (params) => {
  return useQuery({
    queryKey: ["Provider-Bank", params],
    queryFn: () => provider.getProviderBankDetails(params),
    enabled: !_.isEmpty(params),
    retry: false,
  });
};

export const useSaveBankDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params) => provider.saveBankProvider(params),
    retry: false,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["Provider-Bank", variables.userId || ""]);
    },
  });
};

//* Change Password
export const useChangePassword = () => {
  return useMutation({
    mutationFn: (params) => provider.changePassword(params),
    retry: false,
  });
};

//* Clinic Settings (codDisabled / scheduleConsultationDisabled)
export const useGetClinicSettings = (params) => {
  return useQuery({
    queryKey: ["Clinic-Settings", params],
    queryFn: () => provider.getClinicSettings({ providerId: params }),
    enabled: !_.isEmpty(params),
    retry: false,
  });
};

//* Profile Status
export const useGetProfileStatus = (params) => {
  return useQuery({
    queryKey: ["Profile-status", params],
    queryFn: () => provider.getProfileCompletion({ userId: params }),
    enabled: !_.isEmpty(params),
    retry: false,
  });
};

//* Salutation
export const useGetSalutation = () => {
  return useQuery({
    queryKey: ["Get-Salutation"],
    queryFn: () => provider.getSalutation(),
    retry: false,
    select: (data) => ({
      ...data,
      salutations: getDropDownOptions("salutationList", data.salutations || []),
    }),
  });
};

//* Languages
export const useGetLanguages = () => {
  return useQuery({
    queryKey: ["Get-Language"],
    queryFn: () => provider.getLanguages(),
    retry: false,
    select: (data) => ({
      ...data,
      metaDataLanguagesList: getDropDownOptions(
        "languageList",
        data.metaDataLanguagesList || []
      ),
    }),
  });
};

//* Country
export const useGetCountry = () => {
  return useQuery({
    queryKey: ["Get-Country"],
    queryFn: () => provider.getCountry(),
    retry: false,
    select: (data) => ({
      ...data,
      metaDataCountries: getDropDownOptions(
        "countryList",
        data.metaDataCountries || []
      ),
    }),
  });
};

//* Merago Partner Clinics
export const useGetMeragoPartnerClinics = (params) => {
  return useQuery({
    queryKey: ["Get-MeragoPartners", params],
    queryFn: () => provider.getMeragoPartnerClinics({ providerId: params }),
    retry: false,
    select: (data) => ({
      ...data,
      metaDataPartner: getDropDownOptions("partnerList", data || []),
    }),
  });
};

//* States
export const useGetStates = (params) => {
  return useQuery({
    queryKey: ["Get-States", params],
    queryFn: () => provider.getStates({ countryCode: params }),
    enabled: !_.isEmpty(params),
    retry: false,
    select: (data) => ({
      ...data,
      metaDataState: getDropDownOptions("stateList", data.metaDataState || []),
    }),
  });
};

//* Cities
export const useGetCities = (params) => {
  return useQuery({
    queryKey: ["Get-Cities", params],
    queryFn: () => provider.getCities({ stateId: params }),
    enabled: !_.isNil(params),
    retry: false,
    select: (data) => ({
      ...data,
      metaDataCities: getDropDownOptions("cityList", data.metaDataCities || []),
    }),
  });
};

//* Doctor Personal Info
export const useGetDoctorPersonalInfo = (params) => {
  return useQuery({
    queryKey: ["Doctor-PersonalInfo", params],
    queryFn: () => provider.getDoctorPersonalInfo({ providerId: params }),
    enabled: !_.isEmpty(params),
    retry: false,
  });
};

export const useUpdateDoctorPersonalProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params) => provider.updateDoctorPersonalProfile(params),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries(["Profile-status"]);
    },
  });
};

//* DP URL
export const useGetDPUrl = (params) => {
  return useQuery({
    queryKey: ["DP-URL", params],
    queryFn: () => provider.getDPUrl({ userId: params }),
    enabled: !_.isEmpty(params),
    retry: false,
    cacheTime: 0,
  });
};

export const useRemoveDPUrl = () => {
  return useMutation({
    mutationFn: (params) => provider.removeDPUrl(params),
    retry: false,
  });
};

export const useUploadDPUrl = () => {
  return useMutation({
    mutationFn: (params) => provider.uploadDPUrl(params),
    retry: false,
  });
};

//* Signature URL
export const useGetSignatureURL = (params) => {
  return useQuery({
    queryKey: ["Sign-URL", params],
    queryFn: () => provider.getSignURL({ userId: params }),
    enabled: !_.isEmpty(params),
    retry: false,
    cacheTime: 0,
  });
};

export const useRemoveSignUrl = () => {
  return useMutation({
    mutationFn: (params) => provider.removeSignUrl(params),
    retry: false,
  });
};

export const useUploadSignUrl = () => {
  return useMutation({
    mutationFn: (params) => provider.uploadSignUrl(params),
    retry: false,
  });
};

//* Provider Roaster
export const useGetProviderRoaster = (params) => {
  return useQuery({
    queryKey: ["Provider-Roaster", params],
    queryFn: () => provider.getProviderRoster({ providerId: params }),
    enabled: !_.isEmpty(params),
    retry: false,
  });
};

//* Subscribed Hubs
export const useGetSubscribedHubs = (params) => {
  return useQuery({
    queryKey: ["Provider-Hubs", params],
    queryFn: () => provider.getSubscribedHubs({ providerId: params }),
    enabled: !_.isEmpty(params),
    retry: false,
    select: (data) => ({
      ...data,
      meragoHubList: getDropDownOptions("meragoHublistFilter", data.meragoHubList || []),
      meragoUnsubscribedHubList: getDropDownOptions(
        "meragoHublistUnsubscribed",
        data.meragoHubList || []
      ),
    }),
  });
};

export const useGetOnlySubscribedHubsFilter = (params) => {
  return useQuery({
    queryKey: ["Provider-Hubs-Filters", params],
    queryFn: () => provider.getSubscribedHubs({ providerId: params }),
    enabled: !_.isEmpty(params),
    retry: false,
    select: (data) => ({
      ...data,
      meragoHubList: getDropDownOptions("meragoHublistCODFilter", data.meragoHubList || []),
    }),
  });
};

//* Provider Payments
export const useGetProviderPayments = (params) => {
  return useQuery({
    queryKey: ["Provider-Payments", params],
    queryFn: () => provider.getProviderPayments(params),
    enabled: !_.isEmpty(params),
    retry: false,
  });
};

//* Clinic Details
export const useGetClinicDetails = (params) => {
  return useQuery({
    queryKey: ["Provider-Clinics", params],
    queryFn: () => provider.getClinicDetails({ providerId: params }),
    enabled: !_.isEmpty(params),
    retry: false,
  });
};

//* Nurse Clinic Details
export const useGetNurseClinicDetails = (params) => {
  return useQuery({
    queryKey: ["Nurse-Clinics", params],
    queryFn: () => provider.getClinicDetailsByNurseId({ nurseId: params }),
    enabled: !_.isEmpty(params),
    retry: false,
  });
};




//* Patients List
export const useGetPatientsList = (params) => {
  return useQuery({
    queryKey: ["Patients-List", params],
    queryFn: () => provider.getPatientsList(params),
    enabled: !_.isEmpty(params),
    retry: false,
  });
};

//* Member Details
export const useGetMemberDetails = (params) => {
  return useQuery({
    queryKey: ["Member-Details", params],
    queryFn: () => provider.getMemberDetails({ userId: params }),
    enabled: !_.isEmpty(params),
    retry: false,
  });
};

//* Member PCP
export const useGetMemberByUserIdForPcp = (params) => {
  return useQuery({
    queryKey: ["Member-PCP", params],
    queryFn: () => provider.getMemberByUserIdForPcp(params),
    enabled: !_.isEmpty(params),
    retry: false,
  });
};

//* Member & Provider Details
export const useGetMemberProviderDetails = (params) => {
  return useQuery({
    queryKey: ["Member-Provider", params],
    queryFn: () => provider.getMemberProviderDetails(params),
    enabled: !_.isEmpty(params),
    retry: true,
  });
};

//* Provider Service Info
export const useGetProviderServiceInfo = (params) => {
  return useQuery({
    queryKey: ["Service-Info", params],
    queryFn: () => provider.getProviderService({ providerId: params }),
    enabled: !_.isEmpty(params),
    retry: true,
  });
};

//* Normal Service Fee Config
export const useGetNormalServiceFeeConfig = (params) => {
  return useQuery({
    queryKey: ["Service-Fees", params],
    queryFn: () => provider.getProviderNormalServiceFeeConfig({ type: params }),
    enabled: !_.isEmpty(params),
    retry: true,
  });
};

//* UC Service Fee Config
export const useGetUCServiceFeeConfig = (params) => {
  return useQuery({
    queryKey: ["Service-Fees", params],
    queryFn: () => provider.getProviderUCServiceFeeConfig({ type: params }),
    enabled: !_.isEmpty(params),
    retry: true,
  });
};

//* Service Fee Info
export const useGetServiceFeeInfo = (params) => {
  return useQuery({
    queryKey: ["Service-Fees-Info", params],
    queryFn: () => provider.getServiceFeeInfo({ providerId: params }),
    enabled: !_.isEmpty(params),
    retry: true,
  });
};

//* BYOD Login
export const useByodLogin = (params) => {
  return useQuery({
    queryKey: ["Byod-Login", params],
    queryFn: () => byodLogin.byodLoginCall(params),
    enabled: !!params?.username,
    retry: false,
  });
};

//* State Medical Council
export const useGetStateMedicalCoucil = () => {
  return useQuery({
    queryKey: ["State-MedicalCouncil"],
    queryFn: () => provider.getStateMedicalCouncil(),
    retry: false,
    select: (data) => ({
      ...data,
      medicalCouncils: getDropDownOptions("stateMedicalCouncil", data.medicalCouncils || []),
    }),
  });
};

//* Save MRN Details
export const useSaveMRNDetails = () => {
  return useMutation({
    mutationFn: (params) => provider.mrnLicenseSave(params),
    retry: false,
  });
};

//* Clinics Details (Service Fee Info)
export const useGetClinicsDetails = (params) => {
  return useQuery({
    queryKey: ["Service-Clinic-Info", params],
    queryFn: () => provider.getServiceFeeInfo({ providerId: params }),
    enabled: !_.isEmpty(params),
    retry: true,
    select: (data) => ({
      ...data,
      clinicAddressList: getDropDownOptions("clinincDetailsDropdown", data.clinicAddressList || []),
    }),
  });
};

//* Doctor Professional Info
export const useGetDoctoProfessionalInfo = (params) => {
  return useQuery({
    queryKey: ["Povider-ProfessionalInfo", params],
    queryFn: () => provider.getDoctorProfessionalInformation({ providerId: params }),
    enabled: !_.isEmpty(params),
    retry: false,
  });
};

export const useUpdateDoctorProfessionalProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params) => provider.updateDoctorProfessionalProfile(params),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries(["Profile-status"]);
    },
  });
};

//* Specialisation
export const useGetListOfSelectedSpecialization = () => {
  return useQuery({
    queryKey: ["Specialization-List"],
    queryFn: () => provider.getListOfSelectedSpecialization(),
    retry: true,
    select: (data) => ({
      ...data,
      selectedSpecializationList: getDropDownOptions(
        "specialisationSelectedlistDropdown",
        data.selectedSpecializationList || []
      ),
    }),
  });
};

//* Qualifications
export const useGetQualificatiosnList = (params) => {
  return useQuery({
    queryKey: ["Qualifications-List", params],
    queryFn: () => provider.getQualificationslist({ countryCode: params }),
    enabled: !_.isEmpty(params),
    retry: true,
    select: (data) => ({
      ...data,
      metaDataQualifications: getDropDownOptions("qualificationslistDropdown", data.metaDataQualifications || []),
    }),
  });
};

//? Update Provider Service & Fee Info
export const useUpdateProviderServiceFee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params) => provider.saveServiceFeeInfo(params),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries(["Profile-status"]);
      queryClient.invalidateQueries(["Provider-Details"]);
    },
  });
};

//? Send Selected Clinics
export const useSendSelectedClinics = () => {
  return useMutation({
    mutationFn: (params) => provider.getClinicProviderStatus(params),
    retry: false,
  });
};

//? Update Provider Location
export const useUpdateProviderLocation = () => {
  return useMutation({
    mutationFn: (params) => provider.updateProviderLocation(params),
    retry: false,
  });
};

//? Provider Blocks Calendar
export const useBlockCalendar = () => {
  return useMutation({
    mutationFn: (data) => provider.blockCalendar(data),
    retry: false,
  });
};

//? Provider Unblocks Calendar
export const useUnblockCalendar = () => {
  return useMutation({
    mutationFn: (data) => provider.unBlockCalendar(data),
    retry: false,
  });
};

//? Provider Send OTP for Verification
export const useSendOTPForVerification = () => {
  return useMutation({
    mutationFn: (data) => provider.sendOtpToProviderOnLogin(data),
    retry: false,
  });
};

//* Geolocation - Address
export const useGetGeoLocation = (params) => {
  return useQuery({
    queryKey: ["Geo-Location", params],
    queryFn: () => provider.getGoogleMapsByAddess({ address: params }),
    enabled: !_.isEmpty(params),
    retry: true,
  });
};

//* Geolocation - LatLng
export const useGetGeoLocationLatLng = (params) => {
  return useQuery({
    queryKey: ["Geo-LatLng", params],
    queryFn: () => provider.getGoogleGeoCodeLatLng({ latlng: params }),
    enabled: !_.isEmpty(params),
    retry: true,
  });
};

//* Dashboard Menu Status
export const useGetDashboardStatus = () => {
  return useQuery({
    queryKey: ["Dashbaord-MenuStatus"],
    queryFn: () => provider.getDashboardStatus(),
    retry: false,
    refetchInterval: 8000,
    refetchIntervalInBackground: true,
  });
};

//* ABDM Consent Details
export const useGetAbdmProviderConsentDetailsByhealthId = (params) => {
  return useQuery({
    queryKey: ["ABDM-Consent-Details", params],
    queryFn: () => provider.getAbdmProviderConsentDetailsByhealthId({ healthId: params }),
    enabled: !!params,
    retry: false,
    refetchInterval: 10000,
  });
};

//* Video AI Link
export const useGetVideoAiLink = () => {
  return useMutation({
    mutationFn: (params) => provider.getVideoAiLink(params),
    retry: false,
  });
};

//* Consent FHIR Data
export const useGetConsentfhirDataByArtifactId = () => {
  return useMutation({
    mutationFn: (params) => provider.getConsentfhirDataByArtifactId(params),
    retry: false,
  });
};

//? Send Device Id
export const useSendDeviceId = () => {
  return useMutation({
    mutationFn: (data) => provider.deviceToken(data),
    retry: false,
  });
};
